import { useState } from "react";
import { ChevronDown, Copy, Trash2, Baby } from "lucide-react";
import { COUNTRIES, flag, ageOn, flightKind } from "@/lib/api";

export const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="label-lux">{label}</span>
    {children}
  </label>
);

export const PhoneField = ({ code, country, phone, onChange, testId }) => (
  <div className="flex gap-2">
    <select className="input-lux !w-[118px] shrink-0" value={country || "IN"} data-testid={`${testId}-country`}
      onChange={(e) => { const c = COUNTRIES.find((x) => x[0] === e.target.value); onChange({ phone_country: c[0], phone_code: c[2] }); }}>
      {COUNTRIES.map(([iso, name, dial]) => <option key={iso} value={iso}>{flag(iso)} {dial} {name}</option>)}
    </select>
    <input className="input-lux" inputMode="tel" placeholder="98765 43210" value={phone || ""} data-testid={`${testId}-number`}
      onChange={(e) => onChange({ phone: e.target.value.replace(/[^\d\s-]/g, ""), phone_code: code || "+91" })} />
  </div>
);

export const FieldInput = ({ field, value, onChange, testId }) => {
  const v = value ?? "";
  if (field.type === "dropdown") {
    return (
      <select className="input-lux" value={v} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        <option value="">Select…</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.type === "yesno") {
    return (
      <div className="flex gap-2" data-testid={testId}>
        {["Yes", "No"].map((o) => (
          <button type="button" key={o} onClick={() => onChange(o)} data-testid={`${testId}-${o.toLowerCase()}`}
            className={`btn-ghost !py-2 ${v === o ? "!bg-[var(--ink)] !text-[var(--ivory)] !border-[var(--ink)]" : ""}`}>{o}</button>
        ))}
      </div>
    );
  }
  const type = field.type === "number" ? "number" : field.type === "date" ? "date" : "text";
  return <input className="input-lux" type={type} step="any" value={v} onChange={(e) => onChange(e.target.value)} data-testid={testId} />;
};

export const Fold = ({ title, children, defaultOpen = false, action, testId }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-[var(--line)]">
      <div className="flex items-center justify-between gap-2">
        <button type="button" onClick={() => setOpen(!open)} data-testid={testId}
          className="flex flex-1 items-center justify-between py-4 text-left text-[15px]">
          <span>{title}</span>
          <ChevronDown size={18} className={`text-[var(--gold-deep)] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && <div className="pb-5">{action}{children}</div>}
    </div>
  );
};

const NEED_KEYS = ["need_flight_details"];
const BOOKED_KEYS = ["airline_flight", "departing_from", "arrival_info"];

const TravelFields = ({ t = {}, setT, setTravel, tid, admin, cfg }) => {
  const fc = cfg("travel", "flight_choice");
  const keys = [...(t.flight_choice === "need" ? NEED_KEYS : []), ...(t.flight_choice === "booked" ? BOOKED_KEYS : [])].filter((k) => cfg("travel", k));
  return (
  <>
    {fc && <Field label={`${fc.label}${fc.required ? " *" : ""}`} className="sm:col-span-2">
      <select className="input-lux" value={t.flight_choice || ""} data-testid={`${tid}-flight_choice`}
        onChange={(e) => setTravel({ arrival_date: t.arrival_date, arrival_time: t.arrival_time, flight_choice: e.target.value })}>
        <option value="">Select…</option>{fc.options.map((l) => <option key={l} value={flightKind(fc, l)}>{l}</option>)}
      </select>
    </Field>}
    {keys.map((k) => (
      <Field key={k} label={cfg("travel", k).label} className="sm:col-span-2">
        <input className="input-lux" value={t[k] || ""} onChange={(e) => setT(k, e.target.value)} data-testid={`${tid}-${k}`} />
      </Field>
    ))}
    {admin && [["arrival_date", "Confirmed arrival date (organizers)", "date"], ["arrival_time", "Confirmed arrival time (organizers)", "time"]].map(([k, l, ty]) => (
      <Field key={k} label={l}><input type={ty} className="input-lux" value={t[k] || ""} onChange={(e) => setT(k, e.target.value)} data-testid={`${tid}-${k}`} /></Field>
    ))}
  </>
  );
};
const PASSPORT = [
  ["number", "Passport number", "text"], ["nationality", "Nationality", "text"], ["place_of_issue", "Place of issue", "text"],
  ["issue_date", "Issue date", "date"], ["expiry_date", "Expiry date", "date"],
];
const VISA = ["Not required", "To apply", "Applied", "Approved", "Rejected"];

const Grid = ({ children }) => <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>;

const CustomFields = ({ list, values, onSet, tid }) => list.map((f) => (
  <Field key={f.id} label={`${f.label}${f.required ? " *" : ""}`}>
    <FieldInput field={f} value={values?.[f.key]} onChange={(v) => onSet(f.key, v)} testId={`${tid}-field-${f.key}`} />
  </Field>
));

export const PersonCard = ({ person: p, index, onChange, onRemove, onCopyPrev, fields = [], refDate, admin = false }) => {
  const tid = `person-${index}`;
  const set = (patch) => onChange({ ...p, ...patch });
  const setIn = (group, k, v) => onChange({ ...p, [group]: { ...(p[group] || {}), [k]: v } });
  const age = ageOn(p.dob, refDate);
  const byStep = (s) => fields.filter((f) => f.step === s && !f.hidden && !(f.builtin && s !== "costume"));
  const cfg = (step, key) => fields.find((f) => f.step === step && f.key === key && f.builtin && !f.hidden);
  const L = (key, req) => { const f = cfg("personal", key); return f && `${f.label}${req || f.required ? " *" : ""}`; };
  const name = `${p.first_name || ""} ${p.last_name || ""}`.trim();
  return (
    <div className="ivory-card rise p-5 sm:p-7" data-testid={tid}>
      <div className="mb-5 flex items-start justify-between gap-3">
        <div>
          <div className="eyebrow">Person {index + 1}{index === 0 ? " · Main contact" : ""}</div>
          <div className="font-serif-d mt-1 text-2xl" data-testid={`${tid}-name`}>{name || "New person"}</div>
          {age !== null && age < 18 && (
            <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-[var(--ivory-2)] px-3 py-1 text-xs text-[var(--gold-deep)]" data-testid={`${tid}-child-badge`}>
              <Baby size={13} /> Child · {age} yrs
            </span>
          )}
        </div>
        {index > 0 && (
          <button type="button" onClick={onRemove} className="rounded-full p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-700" data-testid={`${tid}-remove`} aria-label="Remove person">
            <Trash2 size={18} />
          </button>
        )}
      </div>
      <Grid>
        {L("first_name") && <Field label={L("first_name", true)}><input className="input-lux" value={p.first_name || ""} onChange={(e) => set({ first_name: e.target.value })} data-testid={`${tid}-first-name`} /></Field>}
        {L("last_name") && <Field label={L("last_name", true)}><input className="input-lux" value={p.last_name || ""} onChange={(e) => set({ last_name: e.target.value })} data-testid={`${tid}-last-name`} /></Field>}
        {L("dob") && <Field label={L("dob")}><input type="date" className="input-lux" max={new Date().toISOString().slice(0, 10)} value={p.dob || ""} onChange={(e) => set({ dob: e.target.value })} data-testid={`${tid}-dob`} /></Field>}
        {L("email") && <Field label={L("email", index === 0)}><input type="email" className="input-lux" value={p.email || ""} onChange={(e) => set({ email: e.target.value })} data-testid={`${tid}-email`} /></Field>}
        {L("phone") && <Field label={L("phone", index === 0)} className="sm:col-span-2">
          <PhoneField code={p.phone_code} country={p.phone_country} phone={p.phone} onChange={set} testId={`${tid}-phone`} />
        </Field>}
        <CustomFields list={byStep("personal")} values={p.custom} onSet={(k, v) => setIn("custom", k, v)} tid={tid} />
      </Grid>
      <div className="mt-6">
        <Fold title="Travel details" testId={`${tid}-travel-toggle`} action={index > 0 && (
          <button type="button" onClick={onCopyPrev} className="btn-ghost mb-4 !py-2 text-[13px]" data-testid={`${tid}-copy-travel`}>
            <Copy size={14} /> Copy {admin ? "details" : "travel details"} from person above
          </button>
        )}>
          <Grid>
            <TravelFields t={p.travel} setT={(k, v) => setIn("travel", k, v)} setTravel={(travel) => set({ travel })} tid={tid} admin={admin} cfg={cfg} />
            <CustomFields list={byStep("travel")} values={p.custom} onSet={(k, v) => setIn("custom", k, v)} tid={tid} />
          </Grid>
        </Fold>
        <Fold title="Costume sizes" testId={`${tid}-costume-toggle`}>
          <Grid><CustomFields list={byStep("costume")} values={p.costume} onSet={(k, v) => setIn("costume", k, v)} tid={`${tid}-costume`} /></Grid>
        </Fold>
        {byStep("extra").length > 0 && (
          <Fold title="A few more questions" testId={`${tid}-extra-toggle`}>
            <Grid><CustomFields list={byStep("extra")} values={p.custom} onSet={(k, v) => setIn("custom", k, v)} tid={tid} /></Grid>
          </Fold>
        )}
        {admin && (
          <Fold title="Passport & visa · organizers only" testId={`${tid}-passport-toggle`}>
            <Grid>
              {PASSPORT.map(([k, l, t]) => (
                <Field key={k} label={l}><input type={t} className="input-lux" value={p.passport?.[k] || ""} onChange={(e) => setIn("passport", k, e.target.value)} data-testid={`${tid}-passport-${k}`} /></Field>
              ))}
              <Field label="Visa status">
                <select className="input-lux" value={p.passport?.visa_status || ""} onChange={(e) => setIn("passport", "visa_status", e.target.value)} data-testid={`${tid}-passport-visa`}>
                  <option value="">Select…</option>{VISA.map((v) => <option key={v}>{v}</option>)}
                </select>
              </Field>
              <Field label="Booking notes" className="sm:col-span-2">
                <textarea className="input-lux min-h-[80px]" value={p.passport?.booking_notes || ""} onChange={(e) => setIn("passport", "booking_notes", e.target.value)} data-testid={`${tid}-passport-notes`} />
              </Field>
            </Grid>
          </Fold>
        )}
      </div>
    </div>
  );
};
